import { useEffect, useState } from "react";

import {
  Button,
  InfoPanel,
  MobileMenu,
  Stepper,
  StepperProvider,
  useStepper,
} from "@king-ict/eupisi-ui/components";
import { useBreakpoint } from "@king-ict/eupisi-ui/hooks";
import { isEmpty } from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import qs from "qs";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useHistory,
  useLocation,
  useRouteMatch,
  useParams,
} from "react-router";
import { useGetRequestCountByChildOibQuery } from "../../api/requestApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import { useCurrentUser } from "../../hooks/authorization/useCurrentUser";
import { useBusinessRule } from "../../hooks/businessRule/useBusinessRule";
import ImportantDateEventEnum from "../../models/ImportantDate/ImportantDateEventEnum";
import ParentNumberEnum from "../../models/Person/ParentNumberEnum";
import RequestTypeEnum from "../../models/Request/RequestTypeEnum";
import { newRequestRoute, newPreschoolRequestRoute } from "../../routes/routes";
import { dashboard as dashboardRoute } from "../../routes/routes";
import {
  getInitialConversation,
  selectInitialConversationQuestionGroupNames,
} from "../../slices/questionnaireSlice";
import {
  getApplicantParentByRequestId,
  getChildByRequestId,
  getDraftRequest,
  getInstitutionByRequestId,
  getOtherParentByRequestId,
  getLocationProgramsByRequestId,
  getRequestCriterionDataByRequestId,
  getInitialConversationByRequestId,
  getRequestBasicInfo,
  setRequestUID,
} from "../../slices/requestSlice";
import ApplicantParentDataStep from "./ApplicantParentDataStep/ApplicantParentDataStep";
import ChildDataStep from "./ChildDataStep/ChildDataStep";
import CriteriaStep from "./CriteriaStep/CriteriaStep";
import InitialConversationStep from "./InitialConversationStep/InitialConversationStep";
import InstitutionDataStep from "./InstitutionDataStep/InstitutionDataStep";
import InstitutionProgramDataStep from "./InstitutionProgramDataStep/InstitutionProgramDataStep";
import OtherParentDataStep from "./OtherParentDataStep/OtherParentDataStep";
import RequestInstitutionHeaderInfo from "./RequestInstitutionHeaderInfo";
import styles from "./RequestPage.module.scss";
import RequestReviewStep from "./RequestReviewStep/RequestReviewStep";
import RequestStepperActions from "./RequestStepperActions";
import RequestStepperHeader from "./RequestStepperHeader";
import { RequestStepperHeaderButtons } from "./RequestStepperHeaderButtons";
import { useRequestBootstrap } from "./useRequestBootstrap";

interface MatchParams {
  id: string;
}

export default function RequestPage() {
  const newRequestMatch = useRouteMatch<MatchParams>(newRequestRoute());
  const newPreschoolRequestMatch = useRouteMatch<MatchParams>(
    newPreschoolRequestRoute()
  );

  const location = useLocation();
  const history = useHistory();
  const params = qs.parse(location.search.substring(1));
  const { applicantParentOib, childOib } = params;
  const { requestId, guid } = useParams<{ requestId: string; guid: string }>();
  const applicantParentOibValue = applicantParentOib?.toString();
  const childOibValue = childOib?.toString();

  const user = useCurrentUser();
  const isNewRequestRoute = Boolean(
    newRequestMatch || newPreschoolRequestMatch
  );
  const newRequest = useTypedSelector((state) => state.request.request);
  const questionGroupNames = useSelector(
    selectInitialConversationQuestionGroupNames
  );
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const [openMenu, setOpenMenu] = useState(false);

  const handleOpenMenu = () => {
    setOpenMenu(true);
  };

  const handleCloseMenu = () => {
    setOpenMenu(false);
  };

  const {
    isRequestEditingAllowed,
    isMaximumRequestCountReached,
  } = useBusinessRule();

  const dispatch = useAppDispatch();
  const { shouldFetchChildRequestCount } = useRequestBootstrap({
    applicantParentOibValue,
    childOibValue,
    history,
    isNewRequestRoute,
    isPreschoolRequestRoute: Boolean(newPreschoolRequestMatch),
  });
  const { data: childRequestCountData } = useGetRequestCountByChildOibQuery(
    {
      childOib: childOibValue ?? "",
      applicantParentOib: applicantParentOibValue,
    },
    {
      skip: !shouldFetchChildRequestCount,
    }
  );
  const childrenRequestsCount = childRequestCountData ?? {
    childOib: childOibValue ?? "",
    requestsCount: 0,
    preschoolRequestsCount: 0,
  };

  useEffect(() => {
    // Edit
    if (
      requestId &&
      !isEmpty(pedagogicalYear.currentImportantDate) &&
      !isEmpty(pedagogicalYear.currentPedagogicalYear)
    ) {
      (async () => {
        await dispatch(getRequestBasicInfo(+requestId))
          .then(unwrapResult)
          .then((item) => {
            if (!isRequestEditingAllowed(item)) {
              history.push(dashboardRoute());
            } else {
              if (item.parentNumberId === ParentNumberEnum.TwoParents) {
                dispatch(
                  getOtherParentByRequestId({
                    requestId: +requestId,
                    refreshData: true,
                  })
                );
              }

              dispatch(
                getApplicantParentByRequestId({
                  requestId: +requestId,
                  refreshData: true,
                })
              );
              dispatch(
                getChildByRequestId({
                  requestId: +requestId,
                  refreshData: true,
                })
              );
              dispatch(getInstitutionByRequestId(+requestId));
              dispatch(getLocationProgramsByRequestId(+requestId));
              dispatch(getRequestCriterionDataByRequestId(+requestId));

              dispatch(getInitialConversation());
              dispatch(getInitialConversationByRequestId(+requestId));
            }
          });
      })();
    }
  }, [
    dispatch,
    history,
    isRequestEditingAllowed,
    requestId,
    pedagogicalYear.currentImportantDate,
    pedagogicalYear.currentPedagogicalYear,
  ]);

  useEffect(() => {
    // Draft
    if (guid) {
      if (
        !isEmpty(pedagogicalYear.currentImportantDate) &&
        pedagogicalYear.currentImportantDate.eventId !==
          ImportantDateEventEnum.RequestCreation
      ) {
        history.push(dashboardRoute());
      } else if (!isEmpty(pedagogicalYear.currentImportantDate)) {
        dispatch(setRequestUID(guid));
        dispatch(getDraftRequest(guid));
        dispatch(getInitialConversation());
      }
    }
  }, [
    dispatch,
    guid,
    history,
    pedagogicalYear.currentImportantDate,
    pedagogicalYear.currentImportantDate.eventId,
  ]);

  const { t } = useTranslation();

  const steps = [
    {
      title: t("request.parentsStep"),
      substeps: [t("request.parentSubStepOne"), t("request.parentSubStepTwo")],
    },
    {
      title: t("request.childStep"),
      substeps: [],
    },
    {
      title: t("request.kindergartenStep"),
      substeps: [
        t("request.kindergartenSubStep"),
        t("request.locationAndProgramSubStep"),
      ],
    },
    {
      title: t("request.criteriaStep"),
      substeps: [],
    },
    {
      title: t("request.initialConversationStep"),
      substeps: questionGroupNames,
    },
    {
      title: t("request.confirmStep"),
      substeps: [],
    },
  ];

  const hasOneParent = () => {
    return (
      isEmpty(newRequest?.parentNumberId) ||
      newRequest.parentNumberId === ParentNumberEnum.OneParent
    );
  };

  const getSteps = () => {
    let newSteps = [...steps];
    if (hasOneParent()) {
      newSteps[0].substeps.splice(1, 1);
    }
    if (user.institutionId) {
      newSteps[2].substeps.splice(0, 1);
    }
    if (
      newRequest.selectedInstitution.usingQuestionnaire === undefined ||
      newRequest.selectedInstitution.usingQuestionnaire === false
    ) {
      newSteps = newSteps.filter(
        (step) => step.title !== t("request.initialConversationStep")
      );
    }
    return newSteps;
  };

  const [stepperState, stepperApi] = useStepper(getSteps());

  const sizeBreakpoint = useBreakpoint();

  function getStepContent(step: number, subStep?: number) {
    switch (step) {
      case 0:
        switch (subStep) {
          case 0:
            return <ApplicantParentDataStep />;
          case 1:
            return <OtherParentDataStep />;
        }
        break;
      case 1:
        return <ChildDataStep />;
      case 2:
        switch (subStep) {
          case 0:
            return user.institutionId ? (
              <InstitutionProgramDataStep />
            ) : (
              <InstitutionDataStep />
            );
          case 1:
            return <InstitutionProgramDataStep />;
        }
        break;
      case 3:
        return <CriteriaStep />;
      case 4:
        if (
          newRequest.selectedInstitution.usingQuestionnaire === undefined ||
          newRequest.selectedInstitution.usingQuestionnaire === false
        ) {
          return (
            <RequestReviewStep moveToSpecificStep={stepperApi.moveToSpecific} />
          );
        }
        return <InitialConversationStep subStep={subStep ?? 0} />;
      case 5:
        return (
          <RequestReviewStep moveToSpecificStep={stepperApi.moveToSpecific} />
        );
      default:
        return <InfoPanel message={"Unknown step"} color="danger" fullWidth />;
    }
  }

  let isMaxRequestCountReached = false;
  if (newRequestMatch) {
    isMaxRequestCountReached = isMaximumRequestCountReached(
      childrenRequestsCount.requestsCount,
      RequestTypeEnum.Kindergarten
    );
  } else if (newPreschoolRequestMatch) {
    isMaxRequestCountReached = isMaximumRequestCountReached(
      childrenRequestsCount.preschoolRequestsCount,
      RequestTypeEnum.Preschool
    );
  }

  return !isMaxRequestCountReached ? (
    <StepperProvider>
      <RequestStepperHeader
        currentStep={stepperState.currentStep}
        currentSubstep={stepperState.currentSubstep}
        openMobileMenu={handleOpenMenu}
      />
      <RequestInstitutionHeaderInfo {...stepperState} />

      <div className={styles.stepperWrapper}>
        {sizeBreakpoint === "xs" || sizeBreakpoint === "sm" ? (
          <Row>
            <MobileMenu open={openMenu} onClose={handleCloseMenu}>
              <div className={styles.mobileMenuButtons}>
                <RequestStepperHeaderButtons
                  currentStep={stepperState.currentStep}
                  currentSubstep={stepperState.currentSubstep}
                />
              </div>
              <aside>
                <Stepper data={getSteps()} {...stepperState} />
              </aside>
            </MobileMenu>
            <Col xs={12}>
              {getStepContent(
                stepperState.currentStep,
                stepperState.currentSubstep
              )}
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={3} className={styles.sidebar}>
              <aside>
                <Stepper data={getSteps()} {...stepperState} />
              </aside>
            </Col>
            <Col md={9} className={styles.content}>
              {getStepContent(
                stepperState.currentStep,
                stepperState.currentSubstep
              )}
            </Col>
          </Row>
        )}
      </div>

      <RequestStepperActions
        stepperState={stepperState}
        stepperApi={stepperApi}
      />
    </StepperProvider>
  ) : (
    <div className={styles.stepperWrapper}>
      <Row>
        <Col xs={12}>
          <InfoPanel
            message={t(
              newPreschoolRequestMatch
                ? "request.maximumPreschoolRequestCountReached"
                : "request.maximumRequestCountReached"
            )}
            color="warning"
            fullWidth
          />
          <Button
            color="primary"
            onClick={() => history.push(dashboardRoute())}
          >
            {t("request.backToDashboard")}
          </Button>
        </Col>
      </Row>
    </div>
  );
}
